import type { ReactElement } from 'react';
import {
  type BoardVisibility,
  type BoardVisibilityPolicyRule,
  type WorkspaceBoardVisibilityPolicy,
  type WorkspaceEffectivePolicySource,
} from '@molt/shared';
import { Button, Menu, Notice, Skeleton, type MenuItem } from '@/components';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

type InterfaceTranslator = InterfaceLanguageContextValue['t'];

export interface WorkspaceBoardVisibilityPolicySectionProps {
  policies: readonly WorkspaceBoardVisibilityPolicy[] | undefined;
  canManage: boolean;
  loading?: boolean | undefined;
  busyKey?: string | null | undefined;
  errors?: Readonly<Record<string, string>> | undefined;
  onChangePolicy: (
    visibility: BoardVisibility,
    field: 'createRule' | 'deleteRule',
    rule: BoardVisibilityPolicyRule,
  ) => void;
}

/**
 * ⚠ The four board-audience nouns are REUSED from `workspaceMembers.*`, not
 * declared again. The catalogue says so at
 * `workspaceMembers.boardPolicy.visibility.private`: this file holds a
 * byte-identical copy of the table the members route announces, and it DRAWS
 * what that route SPEAKS. Two sets of keys would let the two surfaces disagree
 * in every language but English.
 *
 * ⚠ The key suffixes are NOT derived from the wire value — `workspace_visible`
 * keys off `…visibility.workspace` and `enterprise_visible` off
 * `…visibility.enterprise`. Nothing renders the `workspace_visible` arm in the
 * sibling suite, so a swapped arm here is invisible; the arms come from the
 * catalogue, one at a time.
 */
const POLICY_VISIBILITY_KEYS: Record<BoardVisibility, string> = {
  private: 'workspaceMembers.boardPolicy.visibility.private',
  workspace_visible: 'workspaceMembers.boardPolicy.visibility.workspace',
  enterprise_visible: 'workspaceMembers.boardPolicy.visibility.enterprise',
  public: 'workspaceMembers.boardPolicy.visibility.public',
};

const POLICY_RULE_KEYS: Record<BoardVisibilityPolicyRule, string> = {
  any_member: 'workspaceSettings.boardPolicy.rule.anyMember',
  admins_only: 'workspaceSettings.boardPolicy.rule.adminsOnly',
  nobody: 'workspaceSettings.boardPolicy.rule.nobody',
};

const POLICY_RULE_DESCRIPTION_KEYS: Record<BoardVisibilityPolicyRule, string> = {
  any_member: 'workspaceSettings.boardPolicy.ruleDescription.anyMember',
  admins_only: 'workspaceSettings.boardPolicy.ruleDescription.adminsOnly',
  nobody: 'workspaceSettings.boardPolicy.ruleDescription.nobody',
};

const POLICY_RULES = ['any_member', 'admins_only', 'nobody'] as const;

function policyKey(
  visibility: BoardVisibility,
  field: 'createRule' | 'deleteRule',
): string {
  return `${visibility}:${field}`;
}

/**
 * ⚠ THE FOURTH ARM, AND IT IS THE ONE EVERY READER OF THE NAME
 * "three-arm provenance" OMITS.
 *
 * `createRuleSource` and `deleteRuleSource` are
 * `workspaceEffectivePolicySourceSchema.optional()` — three values AND absent,
 * which is four cases, not three. **Absent means fall back to `source`**, which
 * is itself only `'default' | 'workspace'`, so the fold is total and the return
 * type is honest.
 *
 * ⚠ RESOLVED IN ONE PLACE, ON PURPOSE. Both call sites in `PolicyRow` go
 * through this, and `PolicyRuleControl` receives the settled value as a
 * REQUIRED prop. A `?? policy.source` repeated at each render site is one
 * paste away from being repeated wrongly — and the failure is silent, because
 * every fixture in the repository omits these fields, so a dropped fallback
 * renders exactly as today on every test and inverts only under a real
 * Enterprise.
 */
function provenanceOf(
  policy: WorkspaceBoardVisibilityPolicy,
  field: 'createRule' | 'deleteRule',
): WorkspaceEffectivePolicySource {
  const declared =
    field === 'createRule' ? policy.createRuleSource : policy.deleteRuleSource;
  return declared ?? policy.source;
}

/**
 * What is actually enforced on this field.
 *
 * Absent falls back to the workspace-level rule — the value the control already
 * shows — which is also what the server sends whenever the field source is not
 * `'enterprise'`, because `fold()` resolves ties to the workspace.
 */
function effectiveRuleOf(
  policy: WorkspaceBoardVisibilityPolicy,
  field: 'createRule' | 'deleteRule',
): BoardVisibilityPolicyRule {
  return field === 'createRule'
    ? (policy.effectiveCreateRule ?? policy.createRule)
    : (policy.effectiveDeleteRule ?? policy.deleteRule);
}

export function WorkspaceBoardVisibilityPolicySection({
  policies,
  canManage,
  loading = false,
  busyKey = null,
  errors = {},
  onChangePolicy,
}: WorkspaceBoardVisibilityPolicySectionProps): ReactElement {
  const { t } = useInterfaceLanguage();
  /* The `<h2>` and the `<ul>`'s `aria-label` are the same words about the same
     thing, so they resolve ONE key — two would let them drift apart. */
  const heading = t('workspaceSettings.boardPolicy.heading');

  return (
    <section
      className="flex flex-col gap-3"
      aria-labelledby="workspace-board-visibility-policy-heading"
    >
      <div className="flex flex-col gap-1">
        <h2
          id="workspace-board-visibility-policy-heading"
          className="font-display text-lg font-semibold text-ink"
        >
          {heading}
        </h2>
        <p className="text-sm text-ink-muted">
          {t('workspaceSettings.boardPolicy.description')}
        </p>
      </div>

      {loading ? (
        <div role="status" className="flex flex-col gap-2 text-sm text-ink-muted">
          <span>{t('workspaceSettings.boardPolicy.loading')}</span>
          <Skeleton shape="row" count={3} />
        </div>
      ) : null}

      {policies !== undefined && !loading && policies.length === 0 ? (
        <p role="status" className="text-sm text-ink-muted">
          {t('workspaceSettings.boardPolicy.empty')}
        </p>
      ) : null}

      {policies !== undefined && policies.length > 0 ? (
        <ul role="list" aria-label={heading} className="flex flex-col gap-3">
          {policies.map((policy) => (
            <PolicyRow
              key={policy.visibility}
              policy={policy}
              canManage={canManage}
              busyKey={busyKey}
              error={policyError(policy.visibility, errors)}
              onChangePolicy={onChangePolicy}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function PolicyRow({
  policy,
  canManage,
  busyKey,
  error,
  onChangePolicy,
}: {
  policy: WorkspaceBoardVisibilityPolicy;
  canManage: boolean;
  busyKey: string | null;
  error: string | undefined;
  onChangePolicy: WorkspaceBoardVisibilityPolicySectionProps['onChangePolicy'];
}): ReactElement {
  const { t } = useInterfaceLanguage();
  /* Resolved ONCE: the `<h3>` and both `{visibility}` slots below must be the
     same word, or the row heading and its two field labels drift apart. */
  const label = t(POLICY_VISIBILITY_KEYS[policy.visibility]);
  /* ⚠ THE POLARITY TRAP, and it is the reason this is a named constant rather
     than an expression at each of the two call sites below.
     `workspace_admin_can_control` is `NOT NULL DEFAULT true`, so "no Enterprise
     row" and "a permissive Enterprise row" are the SAME answer, and the wire
     field is `.optional()` — absent must therefore read as PERMISSIVE. The
     predicate is `=== false`, never `!policy.workspaceAdminCanControl`, which
     would lock every control on every response that omits the field and produce
     a read-only policy page with nothing red anywhere. `false` only ever means
     an Enterprise admin deliberately set it.

     ⚠ AND IT IS PER ROW, not per section: the Enterprise policy is keyed by
     visibility, so `Private` may be governed while `Public` is not. Hoisting
     this to the section would govern all four rows off whichever row happened
     to carry the `false`. */
  const enterpriseLocked = policy.workspaceAdminCanControl === false;

  return (
    <li className="flex flex-col gap-3 border-b border-border py-3" role="listitem">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold text-ink">{label}</h3>
        {/* ⚠ TWO WHOLE SENTENCES, and the `Source: ` prefix comes out of the
            JSX with them. A fixed prefix glued to a two-word choice fixes word
            order and fixes that the choice is exactly two words; the field is
            the CHOICE OF KEY, and neither arm gets a key of its own. */}
        {/* ⚠ STILL A BINARY TERNARY, AND IT IS NOW BINARY BY CONSTRUCTION
            RATHER THAN BY DEFERRAL. The comment this replaces said it must
            stay binary "while the catalogue is out of this chunk's file list";
            the catalogue is now in reach, the Enterprise arm has landed, and it
            deliberately did NOT land here. Three reasons, in the order they
            were measured:

            1. THE LIVE CATALOGUE MAKES THIS QUESTION ROW-LEVEL.
               `workspace_board_visibility_policies` holds ONE row per
               `(workspace_id, visibility)` carrying BOTH rules, with no
               `workspace_admin_can_control` column at all. So "did a workspace
               row exist" — which is exactly what `source` answers — cannot
               differ between `createRule` and `deleteRule`. Only the
               Enterprise can govern one field and not the other, which is why
               the Enterprise arm is rendered PER FIELD, in
               `PolicyRuleControl`, and this sentence stayed where it is.

            2. `packages/shared/src/workspace.ts:346-354` STATES THIS AS
               CONTRACT, in a file this chunk does not own: `source` may never
               gain a third arm BECAUSE this ternary is binary. Widening the
               ternary would leave a docstring contradicting the code it
               describes.

            3. THE OTHER THREE ARMS WOULD SAY NOTHING. `fold()` on the server
               resolves ties TO THE WORKSPACE (`>`, never `>=`), so a field
               source of `'workspace'`, `'default'` or ABSENT means the
               effective rule is identical to the rule already on the button.
               Rendering a per-field sentence unconditionally would print eight
               captions per page where four carry the information.

            The four arms are all handled and all reachable — they are just
            resolved one level down; `provenanceOf` below is where absent folds
            into `source`. */}
        <p className="text-xs text-ink-muted">
          {policy.source === 'workspace'
            ? t('workspaceSettings.boardPolicy.sourceWorkspace')
            : t('workspaceSettings.boardPolicy.sourceDefault')}
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {/* ⚠ The `{visibility}` NOUN keeps its slot, filled from the table that
            already owns the noun — the opposite ruling from the source pair
            above, because a noun the catalogue declares is a VALUE while a bare
            verb stem would be a FRAGMENT. Eight standalone sentences would let
            the row heading drift from the field label. */}
        <PolicyRuleControl
          visibility={policy.visibility}
          field="createRule"
          label={t('workspaceSettings.boardPolicy.createLabel', { visibility: label })}
          rule={policy.createRule}
          provenance={provenanceOf(policy, 'createRule')}
          effectiveRule={effectiveRuleOf(policy, 'createRule')}
          canManage={canManage}
          enterpriseLocked={enterpriseLocked}
          busyKey={busyKey}
          onChangePolicy={onChangePolicy}
        />
        <PolicyRuleControl
          visibility={policy.visibility}
          field="deleteRule"
          label={t('workspaceSettings.boardPolicy.deleteLabel', { visibility: label })}
          rule={policy.deleteRule}
          provenance={provenanceOf(policy, 'deleteRule')}
          effectiveRule={effectiveRuleOf(policy, 'deleteRule')}
          canManage={canManage}
          enterpriseLocked={enterpriseLocked}
          busyKey={busyKey}
          onChangePolicy={onChangePolicy}
        />
      </div>

      {error !== undefined ? <Notice tone="bad" body={error} /> : null}
    </li>
  );
}

function PolicyRuleControl({
  visibility,
  field,
  label,
  rule,
  provenance,
  effectiveRule,
  canManage,
  enterpriseLocked,
  busyKey,
  onChangePolicy,
}: {
  visibility: BoardVisibility;
  field: 'createRule' | 'deleteRule';
  label: string;
  rule: BoardVisibilityPolicyRule;
  /**
   * Which level imposed this ONE field's effective rule, already folded — see
   * `provenanceOf`. Declared as the settled three-value enum rather than the
   * wire's optional, so the absent case cannot arrive here unresolved.
   *
   * ⚠ Required and plain, never optional-and-spread: a conditional JSX spread
   * is a hole in `tsc`, not a style choice, and the caller always computes it.
   */
  provenance: WorkspaceEffectivePolicySource;
  /** What is actually enforced — `effectiveCreateRule` / `effectiveDeleteRule`. */
  effectiveRule: BoardVisibilityPolicyRule;
  canManage: boolean;
  /**
   * `enterprise_board_visibility_policies.workspace_admin_can_control` is
   * `false` for this row's visibility — `FR-PERM-033`.
   *
   * ⚠ A SECOND, INDEPENDENT REFUSAL, not a restatement of `canManage`.
   * `canManage` is "is this viewer a workspace admin"; this is "may a workspace
   * admin — any workspace admin — touch this at all". Folding the two into one
   * boolean at the caller would lose the distinction the reason text below
   * turns on, and `FR-PERM-033`'s edge case is precisely that the UI must
   * attribute a restriction to the RIGHT level.
   *
   * Declared plainly rather than optional-and-spread: a conditional JSX spread
   * is a hole in `tsc`, not a style choice, and the caller always computes it.
   */
  enterpriseLocked: boolean;
  busyKey: string | null;
  onChangePolicy: WorkspaceBoardVisibilityPolicySectionProps['onChangePolicy'];
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const key = policyKey(visibility, field);
  const busy = busyKey === key;
  const locked = !canManage || enterpriseLocked || busyKey !== null;
  /* ⚠ THE ENTERPRISE ARM NOW RESOLVES A KEY OF ITS OWN, and the comment this
     replaces explained why it could not: the catalogue was outside the chunk's
     file list and declared no Enterprise-attributed lock reason, so a visibly
     disabled control with no reason was chosen over an inverted one. The
     catalogue is now in reach and `boardPolicy.lockedEnterprise` is declared,
     so the control finally says WHO disabled it.

     ⚠ IT IS NOT `boardPolicy.locked`, AND THAT IS THE POINT OF A SECOND KEY.
     That one reads "Only workspace admins can change board visibility policy.",
     and speaking it here would tell a workspace ADMIN that only workspace
     admins may do the thing they have just been refused — the wrong-level
     attribution `FR-PERM-033`'s edge case exists to forbid. The pin asserts the
     two texts differ and that this one never names workspace admins.

     ⚠ `!canManage` STAYS FIRST. It is independently true for a non-admin
     whether or not the Enterprise also governs the row, so reordering would
     replace a correct reason with silence for the commonest refused viewer.

     ⚠ AND BUSY STAYS SECOND, AHEAD OF THE ENTERPRISE ARM. A row that is both
     mid-write and Enterprise-governed must keep saying it is updating; an
     Enterprise arm inserted ahead of busy would mute the only feedback a member
     gets during a write. Both orderings carry their own sibling test. */
  const disabledReason = !canManage
    ? t('workspaceSettings.boardPolicy.locked')
    : busyKey !== null
      ? t('workspaceSettings.boardPolicy.busy')
      : enterpriseLocked
        ? t('workspaceSettings.boardPolicy.lockedEnterprise')
        : undefined;
  /* Resolved once: the accessible name must CONTAIN the visible label. */
  const ruleLabel = t(POLICY_RULE_KEYS[rule]);

  const trigger = (
    <Button
      type="button"
      variant="secondary"
      aria-label={t('workspaceSettings.boardPolicy.ruleControl', {
        rule: ruleLabel,
        field: label,
      })}
      disabled={locked}
      disabledReason={disabledReason}
      busy={busy}
    >
      {ruleLabel}
    </Button>
  );

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-sm font-medium text-ink">{label}</span>
      {/* ⚠ RENDERED ONLY ON THE ENTERPRISE ARM, and the silence on the other
          three is the ruling rather than an omission. `fold()` resolves ties TO
          THE WORKSPACE, so `provenance === 'enterprise'` holds if and only if
          the Enterprise rule is STRICTLY more restrictive than the workspace's
          — which is exactly the case where `effectiveRule` differs from the
          `rule` already drawn on the button below. On `'workspace'`,
          `'default'` and ABSENT the two are equal, the row's own sentence
          already names the level, and a line here would repeat the button in
          words. */}
      {provenance === 'enterprise' ? (
        <p className="text-xs text-ink-muted">
          {t('workspaceSettings.boardPolicy.sourceEnterprise', {
            rule: t(POLICY_RULE_KEYS[effectiveRule]),
          })}
        </p>
      ) : null}
      {/* ⚠ `mt-auto`, AND IT IS A FIX FOR A DEFECT THE SCREENSHOT PASS FOUND
          THAT NO TEST CAN SEE. The caption above is an in-flow block, so a row
          in which ONE field is Enterprise-governed and the other is not — the
          split case this chunk exists to make possible — pushed its two
          controls to different heights. Measured off
          `docs/design/fe13-workspace-policy-provenance/frame-2-*.png` before
          this line existed: the create trigger's top edge sat at y=292 and the
          delete trigger's at y=256, a 36px step, while the un-governed row
          below was flush at 445/445.

          The parent is `grid md:grid-cols-2`, so both columns are already
          stretched to the taller one; `mt-auto` on the last child spends that
          slack ABOVE the control instead of below it, and the two triggers land
          on the same line whether zero, one or two captions are present.

          ⚠ NOT a reserved empty caption slot, which was the other candidate: it
          would print a blank line on all four rows of every un-governed
          workspace — the common case — to tidy the rare one.

          ⚠ AND NOT SEEN BY ANY ASSERTION. The unit suite and the capture both
          count captions; neither reads a position. This is here because someone
          looked at the image. */}
      <div className="mt-auto">
        {locked ? (
          trigger
        ) : (
          <Menu
            trigger={trigger}
            sections={[
              {
                id: key,
                selection: 'radio',
                value: rule,
                onValueChange: (next) =>
                  onChangePolicy(visibility, field, next as BoardVisibilityPolicyRule),
                items: policyItems(t),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}

function policyItems(t: InterfaceTranslator): MenuItem[] {
  return POLICY_RULES.map((rule) => ({
    id: rule,
    label: (
      <span className="flex flex-col items-start gap-0.5 py-0.5 text-left">
        <span className="text-card text-ink">{t(POLICY_RULE_KEYS[rule])}</span>
        <span className="text-xs text-ink-muted">
          {t(POLICY_RULE_DESCRIPTION_KEYS[rule])}
        </span>
      </span>
    ),
    onSelect: () => undefined,
  }));
}

function policyError(
  visibility: BoardVisibility,
  errors: Readonly<Record<string, string>>,
): string | undefined {
  return (
    errors[visibility] ??
    errors[policyKey(visibility, 'createRule')] ??
    errors[policyKey(visibility, 'deleteRule')]
  );
}
